import { useCallback, useEffect, useRef, useState } from 'react'

function sectionErrorMessage(err) {
  const message = err?.response?.data?.message
  if (Array.isArray(message)) {
    const joined = message.filter(Boolean).join(' ')
    if (joined) return joined
  }
  if (typeof message === 'string' && message.trim()) return message
  return 'Failed to load this section'
}

/**
 * Loads one order-detail section the first time it is opened.
 * Later opens reuse the cached payload unless `force` is set.
 */
export function useLazyOrderSection(orderId, loader) {
  const cacheRef = useRef({ orderId: null, data: null, loaded: false })
  const inflightRef = useRef(null)
  const seqRef = useRef(0)
  const loaderRef = useRef(loader)
  loaderRef.current = loader
  const [state, setState] = useState({
    status: 'idle',
    data: null,
    error: null,
  })

  useEffect(() => {
    seqRef.current += 1
    inflightRef.current = null
    cacheRef.current = { orderId, data: null, loaded: false }
    setState({ status: 'idle', data: null, error: null })
  }, [orderId])

  const ensureLoaded = useCallback(
    async ({ force = false } = {}) => {
      if (!orderId) return null
      if (
        !force &&
        cacheRef.current.loaded &&
        cacheRef.current.orderId === orderId
      ) {
        return cacheRef.current.data
      }
      if (!force && inflightRef.current) {
        return inflightRef.current
      }

      const token = ++seqRef.current
      setState((prev) => ({
        status: 'loading',
        data: force ? null : prev.data,
        error: null,
      }))

      const request = loaderRef
        .current(orderId)
        .then((data) => {
          if (token !== seqRef.current) return data
          cacheRef.current = { orderId, data, loaded: true }
          setState({ status: 'ready', data, error: null })
          return data
        })
        .catch((err) => {
          if (token !== seqRef.current) return null
          cacheRef.current = { orderId, data: null, loaded: false }
          setState({
            status: 'error',
            data: null,
            error: sectionErrorMessage(err),
          })
          return null
        })
        .finally(() => {
          if (inflightRef.current === request) inflightRef.current = null
        })

      inflightRef.current = request
      return request
    },
    [orderId],
  )

  return { ...state, ensureLoaded }
}
